import React from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { PremiumAnalyticsDashboard } from '@coffeeplexity/shared';
import { ProgressCircle } from './ProgressCircle';
import { MetricCard } from './MetricCard';
import { AIInsightCard } from './AIInsightCard';
import { BenchmarkComparison } from './BenchmarkComparison';

// const { width } = Dimensions.get('window');

interface PremiumAnalyticsOverviewProps {
  dashboard: PremiumAnalyticsDashboard;
}

export const PremiumAnalyticsOverview: React.FC<
  PremiumAnalyticsOverviewProps
> = ({ dashboard }) => {
  const { learningAnalytics } = dashboard;
  const {
    overallProgress,
    engagementMetrics,
    aiInsights,
    comparativeBenchmarks,
  } = learningAnalytics;

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatStreak = (days: number) => {
    if (days === 1) return '1 day';
    return `${days} days`;
  };

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Overall Progress Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Learning Progress</Text>
        <View style={styles.progressContainer}>
          <View style={styles.progressCircleContainer}>
            <ProgressCircle
              progress={overallProgress.masteryPercentage}
              size={120}
              strokeWidth={8}
              color='#6750A4'
            />
            <View style={styles.progressCenter}>
              <Text style={styles.progressPercentage}>
                {Math.round(overallProgress.masteryPercentage)}%
              </Text>
              <Text style={styles.progressLabel}>Mastery</Text>
            </View>
          </View>

          <View style={styles.progressStats}>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>
                {overallProgress.totalSkillsLearned}
              </Text>
              <Text style={styles.statLabel}>Skills Learned</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>
                {formatTime(overallProgress.timeInvested)}
              </Text>
              <Text style={styles.statLabel}>Time Invested</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>
                {formatStreak(overallProgress.learningStreak)}
              </Text>
              <Text style={styles.statLabel}>Current Streak</Text>
            </View>
          </View>
        </View>
      </View>

      {/* Key Metrics */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Key Metrics</Text>
        <View style={styles.metricsGrid}>
          <MetricCard
            title='Efficiency Score'
            value={overallProgress.efficiencyScore}
            suffix='/100'
            icon='speedometer-outline'
            color='#10B981'
            trend={overallProgress.efficiencyScore > 80 ? 'up' : 'stable'}
          />
          <MetricCard
            title='Confidence'
            value={Math.round(overallProgress.averageConfidence * 100)}
            suffix='%'
            icon='checkmark-circle-outline'
            color='#6750A4'
            trend='up'
          />
          <MetricCard
            title='Session Length'
            value={engagementMetrics.averageSessionLength}
            suffix='min'
            icon='time-outline'
            color='#F59E0B'
            trend={
              engagementMetrics.engagementTrend === 'increasing'
                ? 'up'
                : 'stable'
            }
          />
          <MetricCard
            title='Completion Rate'
            value={engagementMetrics.completionRate}
            suffix='%'
            icon='trophy-outline'
            color='#EF4444'
            trend='up'
          />
        </View>
      </View>

      {/* AI Insights */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>AI Insights</Text>
          <View style={styles.insightsBadge}>
            <Ionicons name='sparkles' size={16} color='#6750A4' />
            <Text style={styles.insightsBadgeText}>
              {aiInsights.length} insights
            </Text>
          </View>
        </View>
        <View style={styles.insightsContainer}>
          {aiInsights.slice(0, 3).map(insight => (
            <AIInsightCard key={insight.id} insight={insight} />
          ))}
          {aiInsights.length > 3 && (
            <TouchableOpacity style={styles.viewMoreInsights}>
              <Text style={styles.viewMoreText}>
                View {aiInsights.length - 3} more insights
              </Text>
              <Ionicons name='chevron-forward' size={16} color='#6750A4' />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Comparative Benchmarks */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Performance Comparison</Text>
        <Text style={styles.sectionSubtitle}>
          See how you compare to other learners
        </Text>
        <View style={styles.benchmarksContainer}>
          {comparativeBenchmarks.map((benchmark, index) => (
            <BenchmarkComparison key={index} benchmark={benchmark} />
          ))}
        </View>
      </View>

      {/* Next Milestone */}
      <View style={styles.section}>
        <View style={styles.milestoneCard}>
          <View style={styles.milestoneHeader}>
            <Ionicons name='flag-outline' size={24} color='#6750A4' />
            <Text style={styles.milestoneTitle}>Next Milestone</Text>
          </View>
          <Text style={styles.milestoneName}>
            {overallProgress.nextMilestone}
          </Text>
          <Text style={styles.milestoneEta}>
            Estimated completion:{' '}
            {new Date(overallProgress.predictedCompletion).toLocaleDateString()}
          </Text>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  section: {
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 4,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  insightsBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EDE7F6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  insightsBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6750A4',
  },
  viewMoreInsights: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
    padding: 16,
    marginTop: 8,
    gap: 4,
  },
  viewMoreText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6750A4',
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 16,
  },
  progressContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  progressCircleContainer: {
    position: 'relative',
    marginRight: 32,
  },
  progressCenter: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressPercentage: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1F2937',
  },
  progressLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  progressStats: {
    flex: 1,
  },
  statItem: {
    marginBottom: 16,
  },
  statValue: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  statLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  insightsContainer: {
    gap: 12,
  },
  benchmarksContainer: {
    gap: 12,
  },
  milestoneCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  milestoneHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  milestoneTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  milestoneName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#6750A4',
    marginBottom: 8,
  },
  milestoneEta: {
    fontSize: 14,
    color: '#6B7280',
  },
});
